import {
    GuestStatus,
    IReservation,
    Label,
    ReservationStatus,
    ReservationUtils,
    StringUtils,
    TIME_FORMAT,
} from '@localina/core';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHaveAccountFeatures } from '../../api/queries/account';
import { GuestStatusIcon } from '../../components/GuestStatusIcon';
import { ReservationAllergyLine } from '../../components/ReservationAllergyLine';
import { ReservationGuestAdditionalInfo } from '../../components/ReservationGuestAdditionalInfo';
import ReservationOriginIcon from '../../components/ReservationOriginIcon/ReservationOriginIcon';
import ReservationStatusActionIcon from '../../components/ReservationStatus/ReservationStatusActionIcon';
import { ReservationTableNumbers } from '../../components/ReservationTableNumbers';
import { GuestUtils } from '../../utils';

interface IProps {
    reservation: IReservation;
    tableNumbers: string[];
    areaCode: string;
    isVirtualArea: boolean;
}

const elements = document.getElementsByClassName('localina-page');

export const ReservationItem: React.FC<IProps> = ({ reservation, tableNumbers, areaCode, isVirtualArea }) => {
    const { t } = useTranslation();
    const className = StringUtils.combineStrings([
        'reservation-list-shiftgroup__reservation-item',
        ReservationUtils.isReservationCancelled(reservation) &&
            'reservation-list-shiftgroup__reservation-item__cancelled',
        reservation.status === ReservationStatus.APPROVAL_PENDING &&
            'reservation-list-shiftgroup__reservation-item__requested',
    ]);
    const [canUseColorIndicator] = useHaveAccountFeatures(['reservationColor']);

    return (
        <div className={className}>
            {Boolean(canUseColorIndicator && reservation.color) && (
                <div
                    className={'reservation-list-shiftgroup__reservation-item__color-indicator'}
                    style={{ backgroundColor: reservation.color! }}
                />
            )}
            <div className="reservation-card">
                <div className="reservation-card__top">
                    <span className="reservation-card__time">
                        {DateTime.fromISO(reservation.reservationDateTime).toFormat(TIME_FORMAT)}
                    </span>
                    <span className="reservation-card__guests">
                        {t('reservations.view.guestsCount', { count: reservation.participants })}
                    </span>
                    <ReservationOriginIcon reservation={reservation} />
                </div>

                <div className="reservation-card__guest">
                    <Label
                        extraClassName={StringUtils.combineStrings([
                            'guest-info',
                            reservation.guestInfo?.guestStatus && 'bold',
                            reservation.guestInfo?.guestStatus === GuestStatus.BLACKLIST && 'error',
                        ])}
                        type="text"
                        value={GuestUtils.formatGuestName(reservation.guestInfo, t('reservations.view.fields.walkin'))}
                    />
                    {Boolean(reservation.guestInfo) && (
                        <GuestStatusIcon guestStatus={reservation.guestInfo?.guestStatus} />
                    )}
                    {reservation.guestInfo && (
                        <ReservationGuestAdditionalInfo
                            reservation={reservation}
                            tooltipContainer={elements.length === 1 ? elements[0] : undefined}
                        />
                    )}
                </div>
                {Boolean(reservation.guestInfo?.allergies) && (
                    <ReservationAllergyLine
                        allergies={reservation.guestInfo!.allergies!}
                        className="reservation-card__allergy"
                    />
                )}

                {}
                <div className="reservation-card__meta">
                    {reservation.areaIds.length > 0 && (
                        <Label error={isVirtualArea} extraClassName="area-code" type="info" value={areaCode} />
                    )}
                    <ReservationTableNumbers
                        tableNumbers={tableNumbers}
                        tooltipContainer={elements.length === 1 ? elements[0] : undefined}
                    />
                </div>
            </div>
            <ReservationStatusActionIcon reservation={reservation} className="reservation-status-box" />
        </div>
    );
};
